import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { prehashPassword } from '@faceup/crypto'
import { ChannelType, Institution } from '@faceup/utils'
import { type InferType, yup } from '@faceup/validation'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import useReportAuth from '../utils/useReportAuth'
import useStorage, { StorageKeys } from '../utils/useStorage'
import { LayoutContext } from './LayoutContext'

const schema = yup
  .object({
    institution: yup
      .object({
        institutionId: yup.string().required(),
        defaultFormId: yup.string().required(),
        type: yup.string().oneOf(Object.values(Institution)).required(),
        channelType: yup.string().oneOf(Object.values(ChannelType)).required(),
        shortLink: yup.string().required(),
        password: yup.string(),
      })
      .nullable(),
    authPin: yup.string().nullable(),
    settings: yup
      .object({
        biometrics: yup.boolean().nullable(),
      })
      .nullable(),
  })
  .required()

export type UserSavedData = InferType<typeof schema>

type State = {
  isAuthenticated: boolean
  login: {
    hasAccount: boolean
    authenticate: (pin: string) => Promise<boolean>
    biometricsAuthentication: () => void
    signUp: (pin: string) => void
  }
  logout: () => Promise<void>
  reportSource: UserSavedData['institution'] | null | undefined
  setReportSource: (reportSource: UserSavedData['institution']) => void
  settings: UserSavedData['settings'] | null
  updateSettings: {
    setBiometrics: (enabled: boolean) => void
  }
  isSetUp: () => boolean
}

export const UserContext = createContext<State>({
  isAuthenticated: false,
  login: {
    hasAccount: false,
    authenticate: async () => false,
    biometricsAuthentication: () => undefined,
    signUp: () => undefined,
  },
  logout: () => Promise.resolve(undefined),
  reportSource: null,
  setReportSource: () => undefined,
  settings: {},
  updateSettings: {
    setBiometrics: () => undefined,
  },
  isSetUp: () => true,
})

const LOGOUT_TIMEOUT = 30000 // 30s
const PIN_SALT = 'cZuUtYfvMpUSgjLKSzDvgw' // RANDOM SALT

type Props = {
  children: ReactNode
}

const UserProvider = ({ children }: Props) => {
  const storage = useStorage()
  const { logout: reportLogout } = useReportAuth()
  const { client } = useContext(LayoutContext)
  const isClientApp = client === 'app'

  const getReportFormData = () => {
    if (!isClientApp) {
      return null
    }
    const item = storage.get(StorageKeys.User)
    if (!item) {
      return null
    }
    const data = JSON.parse(item)
    const isValid = schema.isValidSync(data)
    if (!isValid) {
      storage.remove(StorageKeys.User)
      // We should be informed about invalid data for debugging purposes
      return null
    }
    return data as UserSavedData
  }

  const data = getReportFormData()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authPin, setAuthPin] = useState<UserSavedData['authPin'] | null>(data?.authPin ?? null)
  const [institution, setInstitution] = useState<UserSavedData['institution'] | null>(
    data?.institution ?? null
  )
  const [settings, setSettings] = useState<UserSavedData['settings'] | null>(data?.settings ?? null)

  useEffect(() => {
    // Handles logout if app is no longer active
    let logoutTimeout: number
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        logoutTimeout = window.setTimeout(() => setIsAuthenticated(false), LOGOUT_TIMEOUT)
      } else {
        clearTimeout(logoutTimeout)
      }
    })
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: Storage.set is a stable method from hook
  useEffect(() => {
    if (!isClientApp) {
      return
    }
    const data: UserSavedData = {
      authPin,
      institution,
      settings,
    }
    storage.set(StorageKeys.User, JSON.stringify(data))
  }, [authPin, institution, settings, isClientApp])

  // TODO: Should be refactored (as well as the whole file). Current logic is very fragile
  const updateSettings = (key: string, value: unknown) => {
    const newSettings = { ...(settings ?? {}), [key]: value }
    setSettings(newSettings)
  }

  const prehashPin = async (pin: string) => {
    const prehashedPassword = await prehashPassword({ password: pin, salt: PIN_SALT })
    if (prehashedPassword.isErr()) {
      throw new Error(prehashedPassword.error.message)
    }

    return prehashedPassword.value.passwordKeyPrehash
  }

  const authenticate = async (pin: string) => {
    const prehashedPin = await prehashPin(pin)
    const isPinValid = prehashedPin === authPin
    setIsAuthenticated(isPinValid)
    return isPinValid
  }

  const signUp = async (pin: string) => {
    setAuthPin(await prehashPin(pin))
    setIsAuthenticated(true)
  }

  const logout = async () => {
    await reportLogout()
    setIsAuthenticated(false)
    setInstitution(null)
    setSettings({})
    setAuthPin(null)
    storage.remove(StorageKeys.User)
  }
  const isSetUp = () => {
    if (!isClientApp) {
      return true
    }

    // Hide biometrics for android
    if (Capacitor.getPlatform() !== 'ios') {
      return true
    }

    return settings?.biometrics !== undefined
  }

  return (
    <UserContext.Provider
      value={{
        isAuthenticated: isAuthenticated || client !== 'app',
        login: {
          hasAccount: Boolean(authPin),
          authenticate,
          // When logging with biometrics, no PIN or other information is required, therefore directl login is applied
          biometricsAuthentication: () => setIsAuthenticated(true),
          signUp,
        },
        logout,
        // Institution
        reportSource: institution,
        setReportSource: setInstitution,
        settings,
        updateSettings: {
          setBiometrics: (enabled: boolean) => updateSettings('biometrics', enabled),
        },
        isSetUp,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserProvider
